import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import type {
  DateFormat,
  TimeFormat,
  UndecoratedForm,
} from '@witmetrics/utils';
import { isJsonArray } from '@witmetrics/utils/arrays';
import { isBlank, toTitleCase } from '@witmetrics/utils/strings';
import { formatDateWithTime, formatTextDate } from '@witmetrics/utils/time';
import {
  isBooleanField,
  isDateField,
  isDateTimeField,
} from '@witmetrics/utils/forms';
import type { FieldAnswer } from '@witmetrics/unison-core';
import { styles } from './utils';

export type CustomPdfProps = {
  logoSrc: string;
  form: UndecoratedForm;
  data: FieldAnswer[];
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
};

export default function CustomPdf({
  logoSrc,
  form,
  data,
  dateFormat,
  timeFormat,
}: CustomPdfProps) {
  const renderField = ({ field, value }: FieldAnswer) => {
    let fieldValue = value;
    if (isBlank(value)) {
      fieldValue = '--';
    } else if (isJsonArray(value)) {
      fieldValue = JSON.parse(value).join(', ');
    } else if (isBooleanField(field.formFieldTypeId)) {
      fieldValue = toTitleCase(fieldValue);
    } else if (isDateField(field.formFieldTypeId)) {
      fieldValue = formatTextDate(value, dateFormat);
    } else if (isDateTimeField(field.formFieldTypeId)) {
      fieldValue = formatDateWithTime(value, dateFormat, timeFormat);
    } else if (isDateTimeField(field.formFieldTypeId)) {
      fieldValue = formatDateWithTime(value, dateFormat);
    }
    const isLong = fieldValue.length > 200;
    return (
      <View
        key={field.id}
        style={{
          ...styles.field.wrapper,
          ...(isLong ? styles.field.longWrapper : {}),
        }}>
        <Text style={styles.field.label}>{field.label}</Text>
        <Text
          style={{
            ...styles.field.value,
            ...(isLong ? styles.field.longValue : {}),
          }}>
          {isBlank(fieldValue) ? '--' : fieldValue}
        </Text>
      </View>
    );
  };

  return (
    <Document>
      <Page style={styles.page.wrapper} size="A4">
        <View style={styles.header.wrapper}>
          <Image style={styles.header.logo} src={logoSrc} />
          <View>
            <Text
              style={
                styles.header[form.name.length > 20 ? 'titleLong' : 'title']
              }>
              {form.name}
            </Text>
            <Text style={styles.header.subtitle}>{form.description}</Text>
          </View>
        </View>
        <View style={styles.section.wrapper}>{data.map(renderField)}</View>
      </Page>
    </Document>
  );
}
